import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { showError } from "../utils/toastUtils";

const CartContext = createContext(null);
const MIN_ORDER_AMOUNT = 750;

const buildCartItemId = (serviceId) => serviceId;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("cart")) || [];
    } catch {
      return [];
    }
  });

  const [mobileOpen, setMobileOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
    if (cart.length > 0) setMobileOpen(true);

    if (cart.length === 0) {
      setAppliedCoupon(null);
      setCouponDiscount(0);
    }

  }, [cart]);

  const upsertCartItem = (item) => {
    const cartItemId = buildCartItemId(item.serviceId);

    setCart((prev) => {
      const index = prev.findIndex((i) => i.cartItemId === cartItemId);

      // REMOVE
      if (item.qty === 0) {
        if (index === -1) return prev;
        return prev.filter((_, i) => i !== index);
      }

      const normalized = {
        cartItemId,
        serviceId: item.serviceId,
        title: item.title,
        brand: item.brand ?? null,
        qty: item.qty,
        originalPrice: Number(item.originalPrice),
        discountedPrice: Number(item.discountedPrice),
        imageUrl: item.imageUrl,
      };

      // UPDATE or ADD (same path now)
      if (index !== -1) {
        return prev.map((i, idx) =>
          idx === index ? { ...i, ...normalized } : i,
        );
      }

      return [...prev, normalized];
    });

    setMobileOpen(true);
  };


  const removeService = (serviceId) => {
    setCart((prev) => prev.filter((i) => i.serviceId !== serviceId));
  };

  const clearCart = () => {
    setCart([]);
    setMobileOpen(false);
  };

  const applyCoupon = async ({ code, location }) => {
    try {
      const baseTotal = cart.reduce(
        (s, i) => s + i.discountedPrice * i.qty,
        0
      );

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/coupons/apply`,
        {
          code,
          totalAmount: baseTotal,
          services: cart.map((item) => item.serviceId),
          location,
        }
      );

      setAppliedCoupon(res.data.coupon);
      setCouponDiscount(res.data.discount);

      return { success: true };
    } catch (err) {
      return {
        success: false,
        message:
          err.response?.data?.message || "Failed to apply coupon",
      };
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponDiscount(0);
  };

  const totals = useMemo(() => {
    const totalQty = cart.reduce((s, i) => s + i.qty, 0);

    const subtotal = cart.reduce((s, i) => s + i.originalPrice * i.qty, 0);

    const baseTotal = cart.reduce((s, i) => s + i.discountedPrice * i.qty, 0);

    const totalPrice = Math.max(baseTotal - couponDiscount, 0);

    const savings = Math.max(subtotal - baseTotal, 0);

    const isMinOrderReached = totalPrice >= MIN_ORDER_AMOUNT;

    return {
      totalQty,
      subtotal,
      totalPrice,
      savings,
      couponDiscount,
      appliedCoupon,
      isMinOrderReached,
      remainingToMinOrder: Math.max(MIN_ORDER_AMOUNT - totalPrice, 0),
      minOrderAmount: MIN_ORDER_AMOUNT,
    };
  }, [cart, couponDiscount, appliedCoupon]);

  const getBookingServicesPayload = () => {
    return cart.map((item) => ({
      serviceId: item.serviceId,
      brand: item.brand,
      qty: item.qty,
    }));
  };

  const submitBooking = async (
    bookingData,
    paymentMeta,
    { onSuccess, onError } = {},
  ) => {
    try {
      setSubmitting(true);

      if (!cart.length) throw new Error("Cart is empty");
      if (!bookingData?.slotAvailabilityId)
        throw new Error("Slot not selected");

      const payload = {
        userId: bookingData.userId,
        slotAvailabilityId: bookingData.slotAvailabilityId,
        services: getBookingServicesPayload(),
        paymentMethod: bookingData.paymentMethod,
        location: bookingData.location,
        coupon: appliedCoupon
          ? { code: appliedCoupon.code }
          : null,
      };

      if (bookingData.paymentMethod === "UPI_QR") {
        payload.paymentQR = {
          _id: paymentMeta?.paymentQR?.id,
          upiId: paymentMeta?.paymentQR?.upiId,
          label: paymentMeta?.paymentQR?.label || null,
        };
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/service-bookings/create`,
        payload,
      );

      clearCart();
      onSuccess?.(res.data);
    } catch (err) {
      onError?.(err);
      showError(err.response?.data?.message || "Booking Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        upsertCartItem,
        addService: upsertCartItem,
        removeService,
        clearCart,
        applyCoupon,
        removeCoupon,
        appliedCoupon,
        couponDiscount,
        totals,
        mobileOpen,
        setMobileOpen,
        submitting,
        submitBooking,
        getBookingServicesPayload,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
